import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { LoadingService } from '../../services/loading.service';
import {
  EnrollmentStatusAuditService,
  EnrollmentStatusAuditItem,
  CorporateEnrollmentStatusAuditItem,
  AuditStatusParams,
  EnrollmentAuditBulkFixPreview,
  EnrollmentAuditBulkFixRequest,
  BulkFixAction
} from '../../services/enrollment-status-audit.service';
import { firstValueFrom } from 'rxjs';

type ActiveTab = 'individual' | 'corporate';
type BulkScopeOption = 'selected' | 'filtered';

@Component({
  selector: 'app-enrollment-status-audit',
  templateUrl: './enrollment-status-audit.component.html',
  styleUrls: ['./enrollment-status-audit.component.scss']
})
export class EnrollmentStatusAuditComponent implements OnInit {
  private readonly ALLOWED_EMAIL = 'jdanigo@cloudsoft.site';

  activeTab: ActiveTab = 'individual';

  // Individual state
  individualItems: EnrollmentStatusAuditItem[] = [];
  individualTotal = 0;
  individualLoading = false;

  // Corporate state
  corporateItems: CorporateEnrollmentStatusAuditItem[] = [];
  corporateTotal = 0;
  corporateLoading = false;

  // Shared filters
  filterStatus: number | null = null;
  filterAutoGenerate: boolean | null = true;
  filterSearch = '';
  pageNumber = 1;
  readonly pageSize = 20;

  // Selection across pages
  selectedIndividualIds = new Set<number>();
  selectedCorporateIds = new Set<number>();

  // ===== Bulk fix modal state =====
  showBulkModal = false;
  bulkScope: BulkScopeOption = 'selected';
  bulkAction: BulkFixAction = 'SetAutoGenerate';
  bulkValueBool = false;
  bulkValueStatus: number | null = null;
  bulkComment = '';
  bulkPreview: EnrollmentAuditBulkFixPreview | null = null;
  bulkPreviewLoading = false;
  bulkStarting = false;

  readonly statusOptions = [
    { value: null, label: 'Todos los estados' },
    { value: 0, label: 'Pendiente' },
    { value: 1, label: 'Activo' },
    { value: 2, label: 'Retirado' },
    { value: 3, label: 'Diferido' },
    { value: 4, label: 'Completado' },
    { value: 5, label: 'Refinanciado' },
    { value: 6, label: 'Anulado' },
    { value: 7, label: 'Reembolso' }
  ];

  readonly statusValueOptions = this.statusOptions.filter(o => o.value !== null);

  readonly autoGenerateOptions = [
    { value: null, label: 'Cobro automático: todos' },
    { value: true, label: 'Cobro automático: activo' },
    { value: false, label: 'Cobro automático: inactivo' }
  ];

  constructor(
    private router: Router,
    private authService: AuthService,
    private toast: ToastService,
    private loading: LoadingService,
    private auditService: EnrollmentStatusAuditService
  ) {}

  ngOnInit(): void {
    const email = (this.authService.getCurrentUser()?.email ?? '').toLowerCase();
    if (email !== this.ALLOWED_EMAIL.toLowerCase()) {
      this.router.navigate(['/dashboard']);
      return;
    }
    this.load();
  }

  setTab(tab: ActiveTab): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.pageNumber = 1;
    this.clearSelection();
    this.load();
  }

  load(): void {
    if (this.activeTab === 'individual') {
      this.loadIndividual();
    } else {
      this.loadCorporate();
    }
  }

  private buildParams(): AuditStatusParams {
    return {
      status: this.filterStatus,
      autoGenerate: this.filterAutoGenerate,
      search: this.filterSearch || null,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize
    };
  }

  private async loadIndividual(): Promise<void> {
    this.individualLoading = true;
    try {
      const res = await firstValueFrom(this.auditService.getIndividualAudit(this.buildParams()));
      this.individualItems = res.data;
      this.individualTotal = res.totalRecords;
    } catch {
      this.toast.showToast('error', 'Error cargando inscripciones individuales.');
    } finally {
      this.individualLoading = false;
    }
  }

  private async loadCorporate(): Promise<void> {
    this.corporateLoading = true;
    try {
      const res = await firstValueFrom(this.auditService.getCorporateAudit(this.buildParams()));
      this.corporateItems = res.data;
      this.corporateTotal = res.totalRecords;
    } catch {
      this.toast.showToast('error', 'Error cargando inscripciones empresariales.');
    } finally {
      this.corporateLoading = false;
    }
  }

  onFilterChange(): void {
    this.pageNumber = 1;
    this.load();
  }

  goToPage(page: number): void {
    this.pageNumber = page;
    this.load();
  }

  get totalPages(): number {
    const total = this.activeTab === 'individual' ? this.individualTotal : this.corporateTotal;
    return Math.ceil(total / this.pageSize) || 1;
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    const start = Math.max(1, this.pageNumber - 2);
    const end = Math.min(this.totalPages, start + 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  // ——— Toggle auto-generate individual (mantiene endpoint legacy single-row) ———

  async toggleIndividual(item: EnrollmentStatusAuditItem): Promise<void> {
    const prev = item.autoGeneratePaymentLinks;
    item.autoGeneratePaymentLinks = !prev;
    try {
      await firstValueFrom(this.auditService.setIndividualAutoGenerate(item.id, item.autoGeneratePaymentLinks));
      this.toast.showToast('success', `Cobro automático ${item.autoGeneratePaymentLinks ? 'activado' : 'desactivado'}.`);
    } catch {
      item.autoGeneratePaymentLinks = prev;
      this.toast.showToast('error', 'Error actualizando cobro automático.');
    }
  }

  async toggleCorporate(item: CorporateEnrollmentStatusAuditItem): Promise<void> {
    const prev = item.autoGeneratePaymentLinks;
    item.autoGeneratePaymentLinks = !prev;
    try {
      await firstValueFrom(this.auditService.setCorporateAutoGenerate(item.id, item.autoGeneratePaymentLinks));
      this.toast.showToast('success', `Cobro automático ${item.autoGeneratePaymentLinks ? 'activado' : 'desactivado'}.`);
    } catch {
      item.autoGeneratePaymentLinks = prev;
      this.toast.showToast('error', 'Error actualizando cobro automático.');
    }
  }

  // ——— Selection (persistida entre páginas vía Set) ———

  clearSelection(): void {
    this.selectedIndividualIds.clear();
    this.selectedCorporateIds.clear();
  }

  toggleSelectIndividual(id: number): void {
    if (this.selectedIndividualIds.has(id)) {
      this.selectedIndividualIds.delete(id);
    } else {
      this.selectedIndividualIds.add(id);
    }
  }

  toggleSelectCorporate(id: number): void {
    if (this.selectedCorporateIds.has(id)) {
      this.selectedCorporateIds.delete(id);
    } else {
      this.selectedCorporateIds.add(id);
    }
  }

  selectAllIndividual(): void {
    const allSelected = this.individualItems.every(i => this.selectedIndividualIds.has(i.id));
    if (allSelected) {
      this.individualItems.forEach(i => this.selectedIndividualIds.delete(i.id));
    } else {
      this.individualItems.forEach(i => this.selectedIndividualIds.add(i.id));
    }
  }

  selectAllCorporate(): void {
    const allSelected = this.corporateItems.every(i => this.selectedCorporateIds.has(i.id));
    if (allSelected) {
      this.corporateItems.forEach(i => this.selectedCorporateIds.delete(i.id));
    } else {
      this.corporateItems.forEach(i => this.selectedCorporateIds.add(i.id));
    }
  }

  get allIndividualSelected(): boolean {
    return this.individualItems.length > 0 && this.individualItems.every(i => this.selectedIndividualIds.has(i.id));
  }

  get allCorporateSelected(): boolean {
    return this.corporateItems.length > 0 && this.corporateItems.every(i => this.selectedCorporateIds.has(i.id));
  }

  get selectedCount(): number {
    return this.activeTab === 'individual'
      ? this.selectedIndividualIds.size
      : this.selectedCorporateIds.size;
  }

  get filteredTotal(): number {
    return this.activeTab === 'individual' ? this.individualTotal : this.corporateTotal;
  }

  // ——— Bulk modal ———

  openBulkModal(): void {
    this.bulkScope = this.selectedCount > 0 ? 'selected' : 'filtered';
    this.bulkAction = 'SetAutoGenerate';
    this.bulkValueBool = false;
    this.bulkValueStatus = null;
    this.bulkComment = '';
    this.bulkPreview = null;
    this.showBulkModal = true;
  }

  closeBulkModal(): void {
    this.showBulkModal = false;
    this.bulkPreview = null;
  }

  onBulkActionChange(): void {
    this.bulkPreview = null;
  }

  onBulkScopeChange(): void {
    this.bulkPreview = null;
  }

  private buildBulkRequest(): EnrollmentAuditBulkFixRequest | null {
    if (this.bulkAction === 'SetAutoGenerate') {
      // valueBool always defined (default false)
    } else {
      if (this.bulkValueStatus == null) return null;
    }

    const explicitIds = this.bulkScope === 'selected'
      ? (this.activeTab === 'individual'
          ? Array.from(this.selectedIndividualIds)
          : Array.from(this.selectedCorporateIds))
      : null;

    return {
      scope: this.activeTab,
      action: this.bulkAction,
      valueBool: this.bulkAction === 'SetAutoGenerate' ? this.bulkValueBool : null,
      valueStatus: this.bulkAction === 'SetStatus' ? this.bulkValueStatus : null,
      comment: this.bulkComment.trim(),
      explicitIds: explicitIds && explicitIds.length > 0 ? explicitIds : null,
      filterStatus: this.bulkScope === 'filtered' ? this.filterStatus : null,
      filterAutoGenerate: this.bulkScope === 'filtered' ? this.filterAutoGenerate : null,
      filterSearch: this.bulkScope === 'filtered' ? (this.filterSearch || null) : null
    };
  }

  async loadBulkPreview(): Promise<void> {
    const req = this.buildBulkRequest();
    if (!req) {
      this.toast.showToast('error', 'Selecciona valor para la acción.');
      return;
    }
    this.bulkPreviewLoading = true;
    try {
      this.bulkPreview = await firstValueFrom(this.auditService.previewBulkFix(req));
      if (this.bulkPreview.totalOperations === 0) {
        this.toast.showToast('info', 'No hay inscripciones para procesar con esos criterios.');
      }
    } catch (err: any) {
      const msg = err?.error?.error ?? err?.error?.message ?? err?.error ?? 'Error en preview.';
      this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error en preview.');
    } finally {
      this.bulkPreviewLoading = false;
    }
  }

  get canPreview(): boolean {
    if (this.bulkAction === 'SetStatus' && this.bulkValueStatus == null) return false;
    if (this.bulkScope === 'selected' && this.selectedCount === 0) return false;
    return true;
  }

  get canConfirmBulk(): boolean {
    return !!this.bulkPreview
      && this.bulkPreview.totalOperations > 0
      && !this.bulkStarting
      && this.bulkComment.trim().length >= 10
      && (this.bulkAction === 'SetAutoGenerate' || this.bulkValueStatus != null);
  }

  get bulkBlockReason(): string | null {
    if (this.bulkStarting) return null;
    if (this.bulkScope === 'selected' && this.selectedCount === 0)
      return 'No hay inscripciones seleccionadas. Cambia a "Todos los filtrados" o selecciona filas.';
    if (this.bulkAction === 'SetStatus' && this.bulkValueStatus == null)
      return 'Selecciona el nuevo estado.';
    if (this.bulkComment.trim().length < 10)
      return `Motivo requerido (mín. 10 caracteres, actual: ${this.bulkComment.trim().length}).`;
    if (!this.bulkPreview)
      return 'Carga la vista previa antes de confirmar.';
    if (this.bulkPreview.totalOperations === 0)
      return 'La vista previa no encontró inscripciones para procesar.';
    return null;
  }

  async confirmBulk(): Promise<void> {
    if (!this.canConfirmBulk) return;
    const req = this.buildBulkRequest();
    if (!req) return;
    this.bulkStarting = true;
    this.loading.show();
    try {
      const resp = await firstValueFrom(this.auditService.startBulkFix(req));
      this.closeBulkModal();
      this.clearSelection();
      this.toast.showToast('success', 'Bulk fix iniciado.');
      this.router.navigate(['/dashboard/admin/enrollment-audit/bulk-fix', resp.batchId]);
    } catch (err: any) {
      const msg = err?.error?.error ?? err?.error?.message ?? err?.error ?? 'Error iniciando bulk fix.';
      this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error iniciando bulk fix.');
    } finally {
      this.bulkStarting = false;
      this.loading.hide();
    }
  }

  // ——— Navigation ———

  goToIndividual(id: number): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/dashboard/enrollments/view-v2', id])
    );
    window.open(url, '_blank');
  }

  goToCorporate(id: number): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/dashboard/enrollments/corporate/view-v2', id])
    );
    window.open(url, '_blank');
  }

  // ——— Helpers ———

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      'Pending': 'Pendiente',
      'Active': 'Activo',
      'Withdrawn': 'Retirado',
      'Deferred': 'Diferido',
      'Completed': 'Completado',
      'Refinanced': 'Refinanciado',
      'Reversed': 'Anulado',
      'Refund': 'Reembolso',
      'SlotTransfer': 'Cesión de Cupo'
    };
    return map[status] ?? status;
  }

  statusClass(status: string): string {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800 border-green-300';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Completed': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Reversed': return 'bg-red-100 text-red-800 border-red-300';
      case 'SlotTransfer': return 'bg-teal-100 text-teal-800 border-teal-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  }

  statusValueLabel(value: number | null): string {
    if (value == null) return '—';
    const opt = this.statusValueOptions.find(o => o.value === value);
    return opt ? opt.label : String(value);
  }

  trackById = (_: number, item: { id: number }) => item.id;
}
